import type { DiscountType } from './types';
import type { TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { CURRENCY_REFERENCE, DOLLAR_SIGN, PERCENT_REFERENCE, PERCENT_SIGN } from '../../defaultData';

export const AMOUNT_PATTERN = /^\d+(\.\d*)?$/;

const FOOTER_VALUE_FIELD = 'price';

export function discountTypeToValueFormatter(type: DiscountType, useReferences: boolean): string {
  if (type === 'percentage') return useReferences ? PERCENT_REFERENCE : PERCENT_SIGN;
  return useReferences ? CURRENCY_REFERENCE : DOLLAR_SIGN;
}

export function valueFormatterToDiscountType(valueFormatter: string | undefined): DiscountType {
  return valueFormatter === PERCENT_SIGN || valueFormatter === PERCENT_REFERENCE ? 'percentage' : 'fixed';
}

/**
 * Footer panel's write transform: returns new totalRows with the target row's
 * amount and formatter set as one pair — the panel validates them together, so
 * they must never persist separately.
 */
export function buildFooterRowPairUpdate(totalRows: TableRowType[], rowId: string, amount: string, valueFormatter: string): TableRowType[] {
  return totalRows.map((row) =>
    row.id === rowId
      ? {
          ...row,
          [FOOTER_VALUE_FIELD]: amount,
          cellConfig: { ...row.cellConfig, [FOOTER_VALUE_FIELD]: { ...row.cellConfig?.[FOOTER_VALUE_FIELD], valueFormatter } },
        }
      : row
  );
}

export function formatThousands(value: string): string {
  const [integer, fraction] = value.split('.');
  const withSeparators = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction !== undefined ? `${withSeparators}.${fraction}` : withSeparators;
}

/** Returns a translation key for the first violated rule, or null when valid. */
export function getDiscountValidationErrorKey(raw: string, type: DiscountType, subtotal: string): string | null {
  if (raw === '') return null;
  if (!AMOUNT_PATTERN.test(raw)) return 'pricing.discount.error_invalid';
  // RISK: parseFloat here is UI-threshold validation only — the values
  // themselves stay strings end to end. Never feed these floats onward.
  const numeric = parseFloat(raw);
  if (type === 'percentage' && numeric > 100) return 'pricing.discount.error_max_percentage';
  if (type === 'fixed' && numeric > parseFloat(subtotal || '0')) return 'pricing.discount.error_max_fixed';
  return null;
}
